import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { Moment } from "moment";
import { css } from "@emotion/react";
import {
    DateValidationError,
    PickerChangeHandlerContext
} from "@mui/x-date-pickers";

import {
    BannerType,
    BannerView,
    colors
} from "@dotnet/telematics-design-system";

import DatePicker, { useDatePicker } from "components/DatePicker";
import Autocomplete from "components/DesignComponents/Autocomplete";
import { BUTTON_SIZES } from "components/DesignComponents/Button";
import CopyButton from "components/DesignComponents/CopyButton";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import HighlightedOption from "components/HighlightedOption";
import AuthContext from "context/auth/authContext";
import { INVALID_DATE } from "shared/constants/strings";
import { disableDates, getEnvironment } from "shared/functions";
import { useCompanyLevelSettings } from "shared/hooks/useCompanyLevelSettings";
import { svgFontSize } from "shared/styles/styleFunctions";
import {
    body2SemiBoldStyles,
    body3RegularStyles
} from "shared/styles/typography";
import {
    COMPANY_LEVEL_SETTINGS,
    DROPDOWN_SIZES,
    FEATURE_SETTINGS,
    USER_ACCESS_LEVELS
} from "shared/types/enums";

import { GenerateTokenProps, SELECTED_TOKEN_KEYS, TOKEN_TYPES } from "../types";

const GenerateToken = ({
    name,
    token,
    isLoading,
    change
}: GenerateTokenProps) => {
    const { gray700 } = colors;

    const { t } = useTranslation();
    const { parseDatePickerValue } = useDatePicker();
    const { isFeatureSettingsRestricted } = useCompanyLevelSettings();
    const { user } = useContext(AuthContext);
    const { isProductionEnv } = getEnvironment();

    const [tokenInputValue, setTokenInputValue] = useState("");

    const YEARS = 1;

    const isAuthorizedToCreateIntegrationToken = (
        userAccessLevel: number | undefined
    ) => {
        if (
            isProductionEnv &&
            (userAccessLevel === undefined ||
                userAccessLevel === USER_ACCESS_LEVELS.Support ||
                userAccessLevel === USER_ACCESS_LEVELS.Admin ||
                userAccessLevel === USER_ACCESS_LEVELS.AdminManager)
        ) {
            return false;
        }

        return true;
    };

    const tokenTypes = [
        {
            id: TOKEN_TYPES.Tct,
            name: t(`Api tokens##titles##${TOKEN_TYPES.Tct}##name`),
            description: t(
                `Api tokens##titles##${TOKEN_TYPES.Tct}##description`
            ),
            isRestricted: false
        },
        ...(isAuthorizedToCreateIntegrationToken(user?.access_level)
            ? [
                  {
                      id: TOKEN_TYPES.Integration,
                      name: t(
                          `Api tokens##titles##${TOKEN_TYPES.Integration}##name`
                      ),
                      description: t(
                          `Api tokens##titles##${TOKEN_TYPES.Integration}##description`
                      ),
                      isRestricted: isFeatureSettingsRestricted(
                          FEATURE_SETTINGS.ApiTokenSupport
                      )
                  }
              ]
            : [])
    ];

    const handleExpirationDateChange = (
        dateValue: Moment | null,
        context: PickerChangeHandlerContext<DateValidationError>
    ) => {
        const newValue = parseDatePickerValue(dateValue, context, INVALID_DATE);

        change(SELECTED_TOKEN_KEYS.ExpirationDate, newValue);
    };

    return token ? (
        <div>
            <BannerView
                bannerType={BannerType.Success}
                title={t("Api tokens##token was created")}
            />

            <div
                css={css({
                    display: "flex",
                    alignItems: "flex-end",
                    marginTop: "16px",

                    "& > button": {
                        marginLeft: "8px"
                    }
                })}
            >
                <InputField
                    fullWidth
                    size={INPUT_FIELD_SIZES.Small}
                    labelLeft={t("Api tokens##generated token")}
                    value={token}
                    readOnly
                />

                <CopyButton
                    size={BUTTON_SIZES.Small}
                    copiedValue={token}
                    innerContainerCss={{
                        width: "24px",
                        height: "24px",

                        "& > svg": {
                            ...svgFontSize(16)
                        }
                    }}
                />
            </div>
        </div>
    ) : (
        <>
            <Autocomplete
                fullWidth
                isRequired
                size={DROPDOWN_SIZES.Medium}
                labelLeft={t("Api tokens##token type")}
                textFieldParams={{
                    placeholder: t("Dialog##select")
                }}
                options={tokenTypes}
                getOptionLabel={option => option.name}
                getOptionDisabled={option => option.isRestricted}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                readOnly={isLoading}
                onInputChange={(_, newInputValue) =>
                    setTokenInputValue(newInputValue)
                }
                onChange={(_, value) =>
                    change(SELECTED_TOKEN_KEYS.Type, value?.id ?? null)
                }
                onClose={() => setTokenInputValue("")}
                renderOption={(
                    propsData,
                    { id, name, description, isRestricted }
                ) => {
                    const { key: _key, ...restPropsData } = propsData;

                    return (
                        <TooltipGeneral
                            key={id}
                            title={
                                isRestricted
                                    ? t(
                                          `Restrictions##${COMPANY_LEVEL_SETTINGS.FeatureSettings}##${FEATURE_SETTINGS.ApiTokenSupport}##input option`
                                      )
                                    : ""
                            }
                            placement={TOOLTIP_PLACEMENT.Top}
                        >
                            <li>
                                <span
                                    {...restPropsData}
                                    css={css({
                                        flexDirection: "column",

                                        "&.MuiAutocomplete-option": {
                                            alignItems: "flex-start"
                                        }
                                    })}
                                >
                                    <span css={body2SemiBoldStyles}>
                                        <HighlightedOption
                                            text={name}
                                            query={tokenInputValue}
                                        />
                                    </span>

                                    <span
                                        css={[
                                            body3RegularStyles,
                                            { color: gray700 }
                                        ]}
                                    >
                                        {description}
                                    </span>
                                </span>
                            </li>
                        </TooltipGeneral>
                    );
                }}
            />

            <InputField
                fullWidth
                size={INPUT_FIELD_SIZES.Medium}
                labelLeft={t("Api tokens##name")}
                placeholder={t("Api tokens##example")}
                value={name}
                required
                readOnly={isLoading}
                onChange={e => change(SELECTED_TOKEN_KEYS.Name, e.target.value)}
                customStyle={{ marginTop: "16px" }}
            />

            <DatePicker
                label={`${t("Table##expiration date")} (${t("Dialog##optional")})`}
                disablePast
                readOnly={isLoading}
                shouldDisableYear={date => disableDates(date, YEARS, "years")}
                shouldDisableMonth={date => disableDates(date, YEARS, "years")}
                shouldDisableDate={date => disableDates(date, YEARS, "years")}
                onChange={handleExpirationDateChange}
                customStyle={{ marginTop: "16px" }}
            />
        </>
    );
};

export default GenerateToken;
